import { Link } from 'react-router-dom'
import { Play, Sparkles, ArrowRight, Film, Clapperboard } from 'lucide-react'
import { Button } from '@/components/ui/button'

const originals = [
  {
    title: 'Lagos After Hours',
    tag: 'Political thriller • Binyuma Original',
    logline:
      "A young banker's double life unravels when a secret deal pulls her into Lagos's midnight power games.",
  },
  {
    title: 'Campus Hearts',
    tag: 'Campus romance • Binyuma Original',
    logline:
      'Three roommates navigate love, friendships, and side-hustles on a buzzing African campus – one 12‑minute episode at a time.',
  },
  {
    title: 'Future Accra',
    tag: 'Afro‑futurism • Binyuma Original',
    logline:
      "In a near‑future Accra, a rebel DJ discovers her mixes can bend memory – and threaten the city's most powerful corporation.",
  },
]

export function Originals() {
  return (
    <div className="min-h-screen bg-black text-white">
      <section className="relative max-w-6xl mx-auto px-6 py-16">
        <div className="absolute inset-0 bg-gradient-to-b from-[#4A1FB8]/10 via-black to-black -z-10" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_0,rgba(212,175,55,0.2),transparent_55%),radial-gradient(circle_at_90%_100%,rgba(74,31,184,0.4),transparent_55%)] -z-10" />

        <div className="flex items-center justify-between gap-4 mb-10">
          <Link to="/" className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors">
            &larr; Back to home
          </Link>
          <span className="inline-flex items-center gap-2 text-xs md:text-sm text-gray-400">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            Binyuma Originals – only in the app
          </span>
        </div>

        <div className="grid md:grid-cols-[1.2fr_minmax(0,0.9fr)] gap-10 md:gap-14 items-start mb-16">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1 text-xs font-medium text-[#FFD700] mb-4">
              <Clapperboard className="w-4 h-4" />
              Vertical dramas made for your phone
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
              <span className="bg-gradient-to-r from-[#D4AF37] via-[#FFFFFF] to-[#D4AF37] bg-clip-text text-transparent">
                Binyuma Originals
              </span>
            </h1>
            <p className="text-base md:text-lg text-gray-300 mb-6 max-w-xl">
              Exclusive African series written, shot, and edited for full‑screen 9:16. No recycled TV cuts –
              every frame is designed for the way we actually watch.
            </p>
            <p className="text-sm md:text-base text-gray-400 mb-8 max-w-xl">
              From Lagos thrillers to campus romances and Afro‑futurist epics, Binyuma Originals are the shows
              you can only binge inside the Binyuma TV app.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
              <Link to="/download" className="inline-block">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-[#D72638] hover:bg-[#D72638]/90 text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-[0_0_40px_rgba(215,38,56,0.65)]"
                >
                  Download Binyuma TV
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/signup" className="inline-block">
                <Button
                  size="lg"
                  variant="secondary"
                  className="w-full sm:w-auto border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-6 text-lg font-semibold rounded-xl"
                >
                  Sign up
                </Button>
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="pointer-events-none absolute -inset-6 bg-gradient-to-tr from-[#D72638]/40 via-transparent to-[#FFD700]/40 blur-3xl opacity-70" />
            <div className="relative rounded-[32px] border border-white/10 bg-gradient-to-b from-[#1b1026] via-[#050309] to-black p-[10px] shadow-[0_20px_60px_rgba(0,0,0,0.7)]">
              <div className="rounded-[26px] bg-black overflow-hidden">
                <div className="h-10 flex items-center justify-between px-4 border-b border-white/5 bg-gradient-to-r from-black to-[#120817]">
                  <span className="text-xs text-gray-400">Binyuma Originals</span>
                  <span className="text-[10px] text-gray-500">FULL SCREEN</span>
                </div>
                <div className="aspect-[1/2] bg-gradient-to-b from-[#1B1038] via-[#1E193D] to-black flex flex-col justify-between p-4">
                  <div className="flex justify-between items-start gap-3">
                    <div className="rounded-full bg-black/40 px-3 py-1 text-[10px] uppercase tracking-wide text-gray-200 border border-white/10">
                      New this week
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      <span className="text-[10px] text-gray-300 bg-black/40 px-2 py-1 rounded-full border border-white/5">
                        Lagos After Hours
                      </span>
                      <span className="text-[10px] text-gray-400">Season 1 • Ep 1</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Africa, in full screen.</h3>
                    <p className="text-xs text-gray-300 mb-4 line-clamp-3">
                      Swipe into stories written for your screen – not squeezed into it. Every scene, every cut,
                      every cliffhanger is built for 9:16.
                    </p>
                    <div className="flex items-center justify-between text-[10px] text-gray-300">
                      <span className="inline-flex items-center gap-1">
                        <Play className="w-3 h-3 fill-white" />
                        Swipe up to watch
                      </span>
                      <span className="px-2 py-1 rounded-full bg-white/10 border border-white/20">
                        12 min
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="mt-6 border-t border-gray-900 pt-10">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">A new kind of African series</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {originals.map((show) => (
              <div
                key={show.title}
                className="p-6 rounded-xl bg-gradient-to-br from-gray-900 to-black border border-gray-800 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center gap-2 text-xs text-[#D4AF37] mb-2">
                    <Film className="w-4 h-4" />
                    <span>{show.tag}</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{show.title}</h3>
                  <p className="text-sm text-gray-400 mb-4">{show.logline}</p>
                </div>
                <p className="text-xs text-gray-500">
                  Watch the full series with weekly drops inside the Binyuma TV app.
                </p>
              </div>
            ))}
          </div>
        </section>
      </section>
    </div>
  )
}
